import { Table } from "@tanstack/react-table";
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useCampaignFilterActions } from "../ducks/filter/hooks/actions";
import { useFilterState } from "../ducks/filter/hooks/filterState";

interface CampaignsMeta {
  page: number;
  perPage: number;
  total: number;
  totalPages: number;
}

interface DataTablePaginationProps<TData> {
  table?: Table<TData>;
  meta: CampaignsMeta;
}

export function DataTablePagination<TData>({
  meta,
}: DataTablePaginationProps<TData>) {
  const filterState = useFilterState();
  const { updateFilter } = useCampaignFilterActions();
  const { filters } = filterState || { filters: {} };

  return (
    <div className="flex items-center justify-between px-2">
      <div className="flex-1 text-sm text-muted-foreground">
        {meta.total} linha(s) encontrada(s).
      </div>
      <div className="flex items-center space-x-6 lg:space-x-8">
        <div className="flex items-center space-x-2">
          <p className="text-sm font-medium">Linhas por página</p>
          <Select
            value={`${meta.perPage}`}
            onValueChange={(value) => {
              updateFilter({
                key: "meta",
                value: {
                  ...filters.meta,
                  perPage: parseInt(value),
                  page: 1,
                },
              });
            }}
          >
            <SelectTrigger className="h-8 w-[70px]">
              <SelectValue placeholder={meta.perPage} />
            </SelectTrigger>
            <SelectContent side="top">
              {[10, 20, 30, 40, 50].map((pageSize) => (
                <SelectItem key={pageSize} value={`${pageSize}`}>
                  {pageSize}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex w-[100px] items-center justify-center text-sm font-medium">
          Página {meta.page} de {meta.totalPages}
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            className="hidden h-8 w-8 p-0 lg:flex"
            onClick={() => {
              updateFilter({
                key: "meta",
                value: {
                  ...filters.meta,
                  page: 1,
                },
              });
            }}
            disabled={meta.page === 1}
          >
            <span className="sr-only">Ir para a primeira página</span>
            <ChevronsLeft />
          </Button>
          <Button
            variant="outline"
            className="h-8 w-8 p-0"
            onClick={() => {
              updateFilter({
                key: "meta",
                value: {
                  ...filters.meta,
                  page: (filters.meta?.page || 1) - 1,
                },
              });
            }}
            disabled={meta.page === 1}
          >
            <span className="sr-only">Ir para a página anterior</span>
            <ChevronLeft />
          </Button>
          <Button
            variant="outline"
            className="h-8 w-8 p-0"
            onClick={() => {
              updateFilter({
                key: "meta",
                value: {
                  ...filters.meta,
                  page: (filters.meta?.page || 1) + 1,
                },
              });
            }}
            disabled={meta.page >= meta.totalPages}
          >
            <span className="sr-only">Ir para a próxima página</span>
            <ChevronRight />
          </Button>
          <Button
            variant="outline"
            className="hidden h-8 w-8 p-0 lg:flex"
            onClick={() => {
              updateFilter({
                key: "meta",
                value: {
                  ...filters.meta,
                  page: meta.totalPages,
                },
              });
            }}
            disabled={meta.page >= meta.totalPages}
          >
            <span className="sr-only">Ir para a última página</span>
            <ChevronsRight />
          </Button>
        </div>
      </div>
    </div>
  );
}
